import { useCallback, useMemo } from 'react';

const DECAY_RATE = 0.9; // Score decays by 10% per day
const MS_PER_DAY = 86400000;

const normalizeScores = (value) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};

  return Object.fromEntries(Object.entries(value).filter(([, entry]) => (
    entry
    && typeof entry === 'object'
    && !Array.isArray(entry)
    && Number.isFinite(entry.score)
    && entry.score >= 0
    && Number.isFinite(Date.parse(entry.lastUpdated))
  )));
};

/**
 * Lightweight hook that tracks navigation frequency in localStorage
 * using exponential decay so the toolbar evolves naturally.
 *
 * @param {string} storageKey - localStorage key (e.g. 'adminNavFrequency' or 'memberNavFrequency')
 */
const useNavFrequency = (storageKey) => {
  const readScores = useCallback(() => {
    if (!storageKey || typeof localStorage === 'undefined') return {};
    try {
      return normalizeScores(JSON.parse(localStorage.getItem(storageKey) || '{}'));
    } catch {
      return {};
    }
  }, [storageKey]);

  const writeScores = useCallback((scores) => {
    if (!storageKey || typeof localStorage === 'undefined') return;
    try {
      localStorage.setItem(storageKey, JSON.stringify(scores));
    } catch {
      // Installed PWAs and privacy-restricted browsers can deny storage.
      // Adaptive navigation is optional and must not break the portal shell.
    }
  }, [storageKey]);

  const decayScore = useCallback((entry, now) => {
    const daysSince = (now - new Date(entry.lastUpdated).getTime()) / MS_PER_DAY;
    return entry.score * Math.pow(DECAY_RATE, daysSince);
  }, []);

  const recordVisit = useCallback((sectionId) => {
    const scores = readScores();
    const now = Date.now();
    const existing = scores[sectionId];

    let newScore = 1;
    if (existing) {
      newScore = decayScore(existing, now) + 1;
    }

    scores[sectionId] = {
      score: newScore,
      lastUpdated: new Date(now).toISOString(),
    };

    writeScores(scores);
  }, [readScores, writeScores, decayScore]);

  const getTopSections = useCallback((count, filterFn) => {
    const scores = readScores();
    const now = Date.now();

    const entries = Object.entries(scores)
      .map(([id, entry]) => ({
        id,
        score: decayScore(entry, now),
      }))
      .filter(({ id, score }) => score > 0.01 && (!filterFn || filterFn(id)))
      .sort((a, b) => b.score - a.score)
      .slice(0, count);

    return entries.map(({ id }) => id);
  }, [readScores, decayScore]);

  return useMemo(() => ({
    recordVisit,
    getTopSections,
  }), [recordVisit, getTopSections]);
};

export default useNavFrequency;
